import {
  type DynamicClient,
  InvalidParamError,
} from '@dynamic-labs-sdk/client';
import type {
  ExecuteCheckoutTransactionParams,
  ExecuteCheckoutTransactionResult,
} from '@dynamic-labs-sdk/client/core';
import { getWalletProviderFromWalletAccount } from '@dynamic-labs-sdk/client/core';
import { Psbt } from 'bitcoinjs-lib';

import type { BitcoinSigningPayload } from '../../checkoutTransaction.types';
import { isBitcoinWalletAccount } from '../../isBitcoinWalletAccount';
import { isBitcoinWalletProvider } from '../../isBitcoinWalletProvider';
import { sendRawTransaction } from '../../sendRawTransaction';

export const bitcoinExecuteCheckoutTransaction = async (
  {
    onStepChange,
    signingPayload,
    walletAccount,
  }: ExecuteCheckoutTransactionParams,
  client: DynamicClient
): Promise<ExecuteCheckoutTransactionResult> => {
  const provider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (
    !isBitcoinWalletAccount(walletAccount) ||
    !isBitcoinWalletProvider(provider)
  ) {
    throw new InvalidParamError(
      'Wallet account is not a Bitcoin wallet account'
    );
  }

  const bitcoinPayload = signingPayload as BitcoinSigningPayload;

  if (!bitcoinPayload.psbtTransaction?.psbt) {
    throw new InvalidParamError(
      'Signing payload is missing PSBT transaction data'
    );
  }

  onStepChange?.('transaction');

  const { signedPsbt } = await provider.signPsbt({
    request: {
      allowedSighash: [1],
      unsignedPsbtBase64: bitcoinPayload.psbtTransaction.psbt,
    },
    walletAccount,
  });

  const psbt = Psbt.fromBase64(signedPsbt);

  psbt.finalizeAllInputs();

  const rawTxHex = psbt.extractTransaction().toHex();

  const { transactionId } = await sendRawTransaction(
    { rawTransaction: rawTxHex, walletAccount },
    client
  );

  return { transactionHash: transactionId };
};
